import { styled } from '@mui/material/styles'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox as MUICheckbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { ExportIcon, DeleteIcon } from '~components/icons'
import { COLORS } from '~theme/colors'
import {
  type ChangeEvent,
  type Dispatch,
  type DragEvent,
  type SetStateAction,
  useEffect,
  useState,
} from 'react'
import { fileIsValid } from './tm-document-modal'
import { Z_INDEX } from 'src/types/z-index'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { DocumentTagsField } from './document-tags-field'
import { useOcrDocumentText } from '~hooks/queries/documents/use-ocr-document-text'
import { useExtractCertificateFields } from '~hooks/queries/documents/use-extract-certificate-fields'
import { ALLOWED_EXTENSIONS } from './add-document-modal'
import {
  PRIMARY_CERTIFICATES_TYPE,
  RESTRICTED_CERTIFICATES_TYPE,
} from '../document-types'

function isValidFileExtension(file: File): boolean {
  const filename = file.name
  const extension = filename.split('.').pop()?.toLowerCase()
  return !!extension && ALLOWED_EXTENSIONS.has(extension)
}

export const certificateIssuerOptions = [
  'class',
  'flag',
  'port State',
  'insurance',
  'other',
].map((certificateIssuer) => ({
  label: `${certificateIssuer.charAt(0).toUpperCase()}${certificateIssuer.slice(1)}`,
  value: certificateIssuer.replaceAll(' ', ''),
}))

export const certificateCategories = [
  'Registration & Ownership',
  'Classification',
  'Construction & Load Line',
  'SOLAS (Safety)',
  'ISM (Safety Management)',
  'ISPS (Security)',
  'MARPOL (Environmental)',
  'Crew & Labour',
  'Liability & Insurance',
  'Other (Operational)',
]

export type AUGFile = {
  file: File
  uuid: string
  documentType?: string
  documentName?: string
  documentCategories?: string[]
  isDigitalCertificate?: boolean
  certificateIssuer?: string
  certificateNumber?: string
  referenceNumber?: string
  hasNoExpiryDate?: boolean
  hasNoNextSurvey?: boolean
  issuedDate?: Date | null
  expiryDate?: Date | null
  nextSurveyDate?: Date | null
  lastSurveyDate?: Date | null
  remarks?: string
  tags?: string[]
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

export const TMDocumentModalDropzone = ({
  multiple = true,
  files,
  setFiles,
  primaryDocumentNamesByType,
  existingDocumentTags = [],
}: {
  multiple?: boolean
  files: AUGFile[]
  setFiles: Dispatch<SetStateAction<AUGFile[]>>
  primaryDocumentNamesByType?: {
    documentName: string
    type: string
  }[]
  existingDocumentTags?: string[]
}) => {
  const { setStatus } = useAnimatedAlert()
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())
  const ocrMutation = useOcrDocumentText()
  const extractMutation = useExtractCertificateFields()
  const availableDocumentTags = [
    ...new Set([
      ...existingDocumentTags,
      ...files.flatMap((file) => file.tags || []),
    ]),
  ]

  useEffect(() => {
    return () => {
      files.forEach((file) =>
        URL.revokeObjectURL(URL.createObjectURL(file.file)),
      )
    }
  }, [files])

  const runOcr = async (filesToProcess: AUGFile[]) => {
    const idsToProcess = filesToProcess.map((f) => f.uuid)
    setLoadingIds((prev) => new Set([...prev, ...idsToProcess]))

    try {
      const promiseArray = filesToProcess.map(async (file) => {
        try {
          if (!isValidFileExtension(file.file)) return
          const ocrResult = await ocrMutation.mutateAsync(file.file)
          const prefillResult = await extractMutation.mutateAsync({
            text: ocrResult.text,
            primaryDocumentNames:
              primaryDocumentNamesByType?.map((doc) => doc.documentName) ?? [],
          })

          setFiles((prev) =>
            prev.map((f) =>
              f.uuid === file.uuid
                ? {
                    ...f,
                    documentType: prefillResult.documentType ?? undefined,
                    documentName: prefillResult.documentName ?? undefined,
                    documentCategories:
                      prefillResult.documentCategories ?? undefined,
                    certificateIssuer:
                      prefillResult.certificateIssuer?.replaceAll(' ', '') ??
                      undefined,
                    issuedDate: prefillResult.issueDate
                      ? new Date(prefillResult.issueDate)
                      : null,
                    expiryDate: prefillResult.expiryDate
                      ? new Date(prefillResult.expiryDate)
                      : null,
                    lastSurveyDate: prefillResult.lastSurvey
                      ? new Date(prefillResult.lastSurvey)
                      : null,
                    nextSurveyDate: prefillResult.nextSurvey
                      ? new Date(prefillResult.nextSurvey)
                      : null,
                  }
                : f,
            ),
          )
        } finally {
          setLoadingIds((prev) => {
            const next = new Set(prev)
            next.delete(file.uuid)
            return next
          })
        }
      })

      await Promise.all(promiseArray)
    } catch (err) {
      setLoadingIds(new Set())
      setStatus(
        'Some or all documents failed to prefill. Please fill in the missing information.',
        12000,
        'error',
      )
    }
  }

  return (
    <>
      <Box
        onDrop={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          const droppedFiles: AUGFile[] = Array.from(e.dataTransfer.files).map(
            (f) => ({
              file: f,
              uuid: crypto.randomUUID(),
            }),
          )
          setFiles((prev) =>
            [...droppedFiles, ...prev].slice(multiple ? 0 : -1),
          )
          runOcr(droppedFiles)
        }}
        onDragOver={(e: DragEvent<HTMLDivElement>) => e.preventDefault()}
        onClick={() => {
          const fileInput = document.createElement('input')
          fileInput.type = 'file'
          fileInput.multiple = multiple
          fileInput.onchange = (event: Event) => {
            const selectedFiles: AUGFile[] = Array.from(
              (event.target as HTMLInputElement).files!,
            ).map((f) => ({
              file: f,
              uuid: crypto.randomUUID(),
            }))
            setFiles((prev) =>
              [...selectedFiles, ...prev].slice(multiple ? 0 : -1),
            )
            runOcr(selectedFiles)
          }
          fileInput.click()
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: `1px dashed ${COLORS.greys.mid}`,
          p: 4,
          borderRadius: 2,
          mt: 4,
        }}
      >
        <ExportIcon size={32} />
        <Typography variant="paragraphBold1" sx={{ mt: 1 }}>
          Drag file{multiple ? 's' : ''} here or click to browse
        </Typography>
        <Typography
          variant="paragraph1Secondary"
          sx={{ mb: 2, fontSize: '14px' }}
        >
          Supports{' '}
          {Array.from(ALLOWED_EXTENSIONS)
            .map((ext) => `.${ext}`)
            .join(', ')}{' '}
          files up to 20 MB.
        </Typography>
        <Button
          component="label"
          role={undefined}
          variant="outlined"
          tabIndex={-1}
          sx={{ mt: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          Select file{multiple ? 's' : ''}
          <VisuallyHiddenInput
            type="file"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const selectedFiles: AUGFile[] = Array.from(
                event.target.files || [],
              ).map((f) => ({
                file: f,
                uuid: crypto.randomUUID(),
              }))
              setFiles((prev) =>
                [...selectedFiles, ...prev].slice(multiple ? 0 : -1),
              )
              runOcr(selectedFiles)
            }}
            multiple={multiple}
          />
        </Button>
      </Box>
      {files.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: 4,
            // height: files.length > 4 ? '200px' : 'auto',
            overflowY: 'auto',
            py: 4,
            borderRadius: '8px',
          }}
        >
          {files.map((file, index) => {
            const isLoading = loadingIds.has(file.uuid)
            return (
              <Box
                key={file.uuid}
                sx={{
                  display: 'flex',
                  width: 'fit-content',
                  backgroundColor: COLORS.greys.ultraLight,
                  p: 4,
                  position: 'relative',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={40} />
                  </Box>
                )}
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    p: 1,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '180px',
                    mr: 4,
                    // height: '160px',
                    justifyContent: 'center',
                    textAlign: 'center',
                    // cursor: 'pointer',
                  }}
                  // onClick={(e) => {
                  //   // prevent bubble up
                  //   e.stopPropagation()
                  //   setfile.uuid && setfile.uuid(file.uuid)
                  // }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Chip
                      variant="filled"
                      size="small"
                      label={fileIsValid(file) ? 'Valid' : 'Needs Input'}
                      sx={{
                        pb: '2px',
                        borderRadius: 2,
                        fontWeight: 'bold',
                        backgroundColor: fileIsValid(file)
                          ? COLORS.green.mid
                          : COLORS.orange.mid,
                        color: COLORS.primary.white,
                        fontFamily: 'MaerskTextBold',
                        mb: 2,
                      }}
                    />
                    {file.file.type.startsWith('image/') &&
                    file.file.type !== 'image/heic' ? (
                      <img
                        src={URL.createObjectURL(file.file)}
                        alt={file.file.name}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '80px',
                          height: '80px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${COLORS.greys.mid}`,
                          borderRadius: '8px',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{ userSelect: 'none', fontSize: '24px' }}
                        >
                          .{file.file.name.split('.').pop()?.toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      {file.file.name.length <= 80
                        ? file.file.name
                        : `${file.file.name.slice(0, 80)}...${file.file.name.split('.').pop()}`}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      {(file.file.size / 1024).toFixed(2)} KB
                    </Typography>
                    <IconButton
                      // edge="end"
                      sx={
                        {
                          // position: 'absolute',
                          // bottom: 0,
                          // right: 0,
                        }
                      }
                      size="small"
                      color="error"
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation()
                        setFiles((prev) =>
                          prev.filter((f) => f.uuid !== file.uuid),
                        )
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                  <Box
                    sx={{
                      display: files.length > 0 ? 'flex' : 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      maxWidth: '799px',
                      mt: 7,
                      // mb: 3,
                    }}
                  >
                    <FormControl
                      focused={
                        !files.find((f) => f.uuid === file.uuid)?.documentType
                      }
                      size="small"
                    >
                      <InputLabel id="document-type-label">
                        Document Type
                      </InputLabel>
                      <Select
                        labelId="document-type-label"
                        id="document-type-select"
                        value={
                          files.find((f) => f.uuid === file.uuid)
                            ?.documentType || ''
                        }
                        label="Document Type"
                        onChange={(e) => {
                          setFiles((prev) =>
                            prev.map((f) =>
                              f.uuid === file.uuid
                                ? {
                                    ...f,
                                    documentType: e.target.value,
                                    documentName: '',
                                  }
                                : f,
                            ),
                          )
                        }}
                        MenuProps={{
                          disablePortal: true,
                          PaperProps: {
                            sx: {
                              zIndex: Z_INDEX.Popup + 1,
                            },
                          },
                        }}
                        sx={{ width: 344 }}
                      >
                        {[
                          {
                            label: 'Primary Certificates',
                            value: 'Primary Certificates',
                          },
                          {
                            label: 'Primary Certificates (Other)',
                            value: 'Primary Certificates (Other)',
                          },
                          {
                            label: 'Secondary Certificates',
                            value: 'Secondary Certificates',
                          },
                          ...(primaryDocumentNamesByType?.some(
                            ({ type }) => type === RESTRICTED_CERTIFICATES_TYPE,
                          )
                            ? [
                                {
                                  label: RESTRICTED_CERTIFICATES_TYPE,
                                  value: RESTRICTED_CERTIFICATES_TYPE,
                                },
                              ]
                            : []),
                        ].map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {files.find((f) => f.uuid === file.uuid)?.documentType ===
                      'Secondary Certificates' ||
                    files.find((f) => f.uuid === file.uuid)?.documentType ===
                      'Primary Certificates (Other)' ? (
                      <TextField
                        size="small"
                        name="documentName"
                        label="Document Name"
                        required
                        sx={{ width: 344 }}
                        disabled={
                          !files.find((f) => f.uuid === file.uuid)?.documentType
                        }
                        value={
                          files.find((f) => f.uuid === file.uuid)
                            ?.documentName || ''
                        }
                        onChange={(e) => {
                          setFiles((prev) =>
                            prev.map((f) =>
                              f.uuid === file.uuid
                                ? { ...f, documentName: e.target.value }
                                : f,
                            ),
                          )
                        }}
                      />
                    ) : (
                      <FormControl size="small" sx={{ minWidth: 344 }}>
                        <InputLabel id="document-name-label">
                          Document Name
                        </InputLabel>
                        <Select
                          value={
                            files.find((f) => f.uuid === file.uuid)
                              ?.documentName || ''
                          }
                          onChange={(e) => {
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, documentName: e.target.value }
                                  : f,
                              ),
                            )
                          }}
                          required
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType
                          }
                          sx={{ width: 344 }}
                          MenuProps={{
                            disablePortal: true,
                          }}
                          label="Document Name"
                        >
                          {primaryDocumentNamesByType
                            ?.filter(
                              ({ type }) =>
                                type ===
                                files.find((f) => f.uuid === file.uuid)
                                  ?.documentType,
                            )
                            .map((option) => (
                              <MenuItem
                                key={option.documentName}
                                value={option.documentName}
                              >
                                {option.documentName}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                    <Autocomplete
                      disabled={
                        !files.find((f) => f.uuid === file.uuid)?.documentType
                      }
                      limitTags={1}
                      size="small"
                      multiple
                      disableCloseOnSelect
                      options={certificateCategories}
                      value={
                        files.find((f) => f.uuid === file.uuid)
                          ?.documentCategories || []
                      }
                      onChange={(_, newValue) => {
                        setFiles((prev) =>
                          prev.map((f) =>
                            f.uuid === file.uuid
                              ? { ...f, documentCategories: newValue }
                              : f,
                          ),
                        )
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            {...getTagProps({ index })}
                            key={option}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Document Categories"
                          required={
                            files.find((f) => f.uuid === file.uuid)
                              ?.documentType === PRIMARY_CERTIFICATES_TYPE
                          }
                        />
                      )}
                      sx={{ minWidth: 344 }}
                      disablePortal
                    />
                    <FormControl size="small" sx={{ minWidth: 344 }}>
                      <InputLabel id="certificate-issuer-label">
                        Certificate Issuer
                      </InputLabel>
                      <Select
                        name="certificateIssuer"
                        value={
                          files.find((f) => f.uuid === file.uuid)
                            ?.certificateIssuer || ''
                        }
                        onChange={(e) =>
                          setFiles((prev) =>
                            prev.map((f) =>
                              f.uuid === file.uuid
                                ? { ...f, certificateIssuer: e.target.value }
                                : f,
                            ),
                          )
                        }
                        required={
                          files.find((f) => f.uuid === file.uuid)
                            ?.documentType === PRIMARY_CERTIFICATES_TYPE
                        }
                        disabled={
                          !files.find((f) => f.uuid === file.uuid)?.documentType
                        }
                        sx={{ width: 344 }}
                        MenuProps={{
                          disablePortal: true,
                        }}
                        label="Certificate Issuer"
                      >
                        {certificateIssuerOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <DocumentTagsField
                      disabled={
                        !files.find((f) => f.uuid === file.uuid)?.documentType
                      }
                      disablePortal
                      documentTags={
                        files.find((f) => f.uuid === file.uuid)?.tags || []
                      }
                      existingDocumentTags={availableDocumentTags}
                      onChange={(newTags) => {
                        setFiles((prev) =>
                          prev.map((f) =>
                            f.uuid === file.uuid ? { ...f, tags: newTags } : f,
                          ),
                        )
                      }}
                      showDescription={false}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        display: files.length > 0 ? 'flex' : 'none',
                        // flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        maxWidth: '799px',
                        mb: 2,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MUICheckbox
                            disabled={
                              !files.find((f) => f.uuid === file.uuid)
                                ?.documentType
                            }
                            checked={
                              files.find((f) => f.uuid === file.uuid)
                                ?.isDigitalCertificate || false
                            }
                            onChange={(e) =>
                              setFiles((prev) =>
                                prev.map((f) =>
                                  f.uuid === file.uuid
                                    ? {
                                        ...f,
                                        isDigitalCertificate: e.target.checked,
                                      }
                                    : f,
                                ),
                              )
                            }
                          />
                        }
                        label="Digital/Electronic Certificate"
                      />
                      <FormControlLabel
                        control={
                          <MUICheckbox
                            disabled={
                              !files.find((f) => f.uuid === file.uuid)
                                ?.documentType
                            }
                            checked={
                              files.find((f) => f.uuid === file.uuid)
                                ?.hasNoExpiryDate || false
                            }
                            onChange={(e) => {
                              setFiles((prev) =>
                                prev.map((f) =>
                                  f.uuid === file.uuid
                                    ? {
                                        ...f,
                                        hasNoExpiryDate: e.target.checked,
                                        expiryDate: e.target.checked
                                          ? null
                                          : f.expiryDate,
                                      }
                                    : f,
                                ),
                              )
                            }}
                          />
                        }
                        label="No Expiry Date"
                      />
                      <FormControlLabel
                        control={
                          <MUICheckbox
                            disabled={
                              !files.find((f) => f.uuid === file.uuid)
                                ?.documentType
                            }
                            checked={
                              files.find((f) => f.uuid === file.uuid)
                                ?.hasNoNextSurvey || false
                            }
                            onChange={(e) => {
                              setFiles((prev) =>
                                prev.map((f) =>
                                  f.uuid === file.uuid
                                    ? {
                                        ...f,
                                        hasNoNextSurvey: e.target.checked,
                                        nextSurveyDate: e.target.checked
                                          ? null
                                          : f.nextSurveyDate,
                                      }
                                    : f,
                                ),
                              )
                            }}
                          />
                        }
                        label="No Next Survey"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                      <Box
                        sx={{
                          display: files.length > 0 ? 'flex' : 'none',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 4,
                          // mb: 4,
                        }}
                      >
                        <DatePicker
                          slotProps={{
                            textField: {
                              required:
                                files.find((f) => f.uuid === file.uuid)
                                  ?.documentType === PRIMARY_CERTIFICATES_TYPE,
                              size: 'small',
                            },
                            popper: {
                              disablePortal: true,
                            },
                          }}
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType
                          }
                          value={
                            files.find((f) => f.uuid === file.uuid)
                              ?.issuedDate || null
                          }
                          onChange={(newValue) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, issuedDate: newValue }
                                  : f,
                              ),
                            )
                          }
                          label="Issue Date"
                          sx={{ width: 344 }}
                        />
                        <DatePicker
                          slotProps={{
                            textField: {
                              required:
                                files.find((f) => f.uuid === file.uuid)
                                  ?.documentType ===
                                  PRIMARY_CERTIFICATES_TYPE &&
                                !files.find((f) => f.uuid === file.uuid)
                                  ?.hasNoExpiryDate,
                              size: 'small',
                            },
                            popper: {
                              disablePortal: true,
                            },
                          }}
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType ||
                            files.find((f) => f.uuid === file.uuid)
                              ?.hasNoExpiryDate
                          }
                          value={
                            files.find((f) => f.uuid === file.uuid)
                              ?.expiryDate || null
                          }
                          onChange={(newValue) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, expiryDate: newValue }
                                  : f,
                              ),
                            )
                          }
                          label="Expiry Date"
                          sx={{ width: 344 }}
                        />
                        <DatePicker
                          slotProps={{
                            textField: {
                              required:
                                files.find((f) => f.uuid === file.uuid)
                                  ?.documentType === PRIMARY_CERTIFICATES_TYPE,
                              size: 'small',
                            },
                            popper: {
                              disablePortal: true,
                            },
                          }}
                          value={
                            files.find((f) => f.uuid === file.uuid)
                              ?.lastSurveyDate || null
                          }
                          onChange={(newValue) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, lastSurveyDate: newValue }
                                  : f,
                              ),
                            )
                          }
                          label="Last Survey"
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType
                          }
                          sx={{ width: 344 }}
                        />
                        <DatePicker
                          slotProps={{
                            textField: {
                              required:
                                files.find((f) => f.uuid === file.uuid)
                                  ?.documentType ===
                                  PRIMARY_CERTIFICATES_TYPE &&
                                !files.find((f) => f.uuid === file.uuid)
                                  ?.hasNoNextSurvey,
                              size: 'small',
                            },
                            popper: {
                              disablePortal: true,
                            },
                          }}
                          value={
                            files.find((f) => f.uuid === file.uuid)
                              ?.nextSurveyDate || null
                          }
                          onChange={(newValue) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, nextSurveyDate: newValue }
                                  : f,
                              ),
                            )
                          }
                          label="Next Survey"
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType ||
                            files.find((f) => f.uuid === file.uuid)
                              ?.hasNoNextSurvey
                          }
                          sx={{ width: 344 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: files.length > 0 ? 'flex' : 'none',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 4,
                          // mb: 4,
                        }}
                      >
                        <TextField
                          sx={{ width: 344 }}
                          label="Certificate Number"
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType
                          }
                          value={
                            files.find((f) => f.uuid === file.uuid)
                              ?.certificateNumber || ''
                          }
                          onChange={(e) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, certificateNumber: e.target.value }
                                  : f,
                              ),
                            )
                          }
                          size="small"
                        />
                        <TextField
                          sx={{ width: 344 }}
                          label="Reference number to physical filing system"
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType
                          }
                          value={
                            files.find((f) => f.uuid === file.uuid)
                              ?.referenceNumber || ''
                          }
                          onChange={(e) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, referenceNumber: e.target.value }
                                  : f,
                              ),
                            )
                          }
                          size="small"
                        />
                        <TextField
                          multiline
                          minRows={4}
                          maxRows={4}
                          sx={{ width: 344 }}
                          value={
                            files.find((f) => f.uuid === file.uuid)?.remarks ||
                            ''
                          }
                          onChange={(e) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.uuid === file.uuid
                                  ? { ...f, remarks: e.target.value }
                                  : f,
                              ),
                            )
                          }
                          label="Remarks"
                          disabled={
                            !files.find((f) => f.uuid === file.uuid)
                              ?.documentType
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Box>
      )}
    </>
  )
}

export default TMDocumentModalDropzone
